<template>
  <div style="max-width: 90%; margin: auto" class="py-10">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-6 flex-wrap" style="gap: 16px">
      <div>
        <p class="page-subtitle mb-1">Support</p>
        <h1 class="page-title">Live Chat</h1>
      </div>

      <div class="stat-row">
        <div class="stat-card stat-toggle">
          <div>
            <div class="stat-label">Live Chat</div>
            <div class="stat-value">{{ chatEnabled ? 'Aktiviert' : 'Deaktiviert' }}</div>
          </div>
          <button
            type="button"
            class="toggle-switch"
            :class="{ 'toggle-on': chatEnabled }"
            :disabled="toggling"
            @click="toggleChat(!chatEnabled)"
          >
            <v-progress-circular v-if="toggling" indeterminate size="12" width="2" :color="chatEnabled ? 'white' : '#888'" />
            <div v-else class="toggle-thumb"></div>
          </button>
        </div>
        <div class="stat-card">
          <div class="stat-label">Aktiv</div>
          <div class="stat-value-row">
            <span class="stat-dot stat-dot-active"></span>
            <span class="stat-value">{{ activeCount }}</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Wartend</div>
          <div class="stat-value-row">
            <span class="stat-dot stat-dot-waiting"></span>
            <span class="stat-value">{{ waitingCount }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Alert -->
    <div v-if="!chatEnabled" class="alert-banner mb-5">
      <div class="alert-icon"><v-icon size="18" color="#E65100">mdi-alert-outline</v-icon></div>
      <div>
        <div class="alert-title">Live Chat ist deaktiviert</div>
        <div class="alert-sub">Das Widget wird Kunden aktuell nicht angezeigt.</div>
      </div>
    </div>

    <!-- Chat Container -->
    <div class="chat-container">
      <!-- Conversations Column -->
      <div class="conversations-column">
        <div class="conv-header">
          <div class="conv-title">Offene Chats</div>
          <div class="seg-toggle mt-3">
            <button class="seg-btn" :class="{ 'seg-active': filter === 'all' }" @click="filter = 'all'">Alle</button>
            <button class="seg-btn" :class="{ 'seg-active': filter === 'waiting' }" @click="filter = 'waiting'">
              Wartend
              <span v-if="waitingCount > 0" class="seg-count seg-count-warning">{{ waitingCount }}</span>
            </button>
            <button class="seg-btn" :class="{ 'seg-active': filter === 'active' }" @click="filter = 'active'">
              Aktiv
              <span v-if="activeCount > 0" class="seg-count seg-count-success">{{ activeCount }}</span>
            </button>
          </div>
        </div>

        <div class="conv-list">
          <button
            v-for="conv in filteredConversations"
            :key="conv.id"
            type="button"
            class="conv-item"
            :class="{
              'conv-active': selectedConversation?.id === conv.id,
              'conv-unread': conv.unreadCount > 0,
            }"
            @click="handleConversationClick(conv)"
          >
            <div class="conv-avatar" :class="`conv-avatar-${conv.status}`">
              {{ conv.customerName.charAt(0).toUpperCase() }}
            </div>

            <div class="conv-content">
              <div class="conv-row">
                <span class="conv-name">{{ conv.customerName }}</span>
                <span class="conv-time">{{ formatTime(conv.updatedAt) }}</span>
              </div>
              <div class="conv-preview">{{ conv.lastMessage?.message || 'Keine Nachrichten' }}</div>
            </div>

            <div class="conv-badges">
              <span v-if="conv.unreadCount > 0" class="unread-badge">{{ conv.unreadCount }}</span>
              <div class="status-dot" :class="`status-${conv.status}`"></div>
            </div>
          </button>

          <div v-if="filteredConversations.length === 0" class="empty-state-inline">
            <v-icon size="40" color="#c5cbd3" class="mb-2">mdi-chat-outline</v-icon>
            <div class="empty-desc">Keine Conversations gefunden</div>
          </div>
        </div>
      </div>

      <!-- Chat Column -->
      <div class="chat-column">
        <div v-if="!selectedConversation" class="chat-empty">
          <v-icon size="48" color="#c5cbd3" class="mb-3">mdi-message-text-outline</v-icon>
          <h3 class="empty-title">Wähle einen Chat aus</h3>
          <p class="empty-desc">Klicke links auf eine Conversation um zu starten.</p>
        </div>

        <template v-else>
          <div class="chat-header">
            <div class="chat-header-user">
              <div class="conv-avatar conv-avatar-header">
                {{ selectedConversation.customerName.charAt(0).toUpperCase() }}
              </div>
              <div>
                <div class="chat-user-name">{{ selectedConversation.customerName }}</div>
                <div class="chat-user-sub">{{ selectedConversation.customerEmail || selectedConversation.shopDomain }}</div>
              </div>
            </div>

            <div class="chat-header-actions">
              <button
                v-if="!isCurrentAgentInConversation && selectedConversation.status !== 'closed'"
                class="btn-action btn-action-success"
                :disabled="currentlyJoinedConversationId !== null"
                @click="confirmTakeConversation"
              >
                <v-icon size="15">mdi-account-check-outline</v-icon>
                <span>Übernehmen</span>
              </button>
              <button
                v-if="isCurrentAgentInConversation"
                class="btn-action btn-action-warning"
                @click="leaveConversation"
              >
                <v-icon size="15">mdi-exit-run</v-icon>
                <span>Verlassen</span>
              </button>
              <button
                v-if="isCurrentAgentInConversation"
                class="btn-action btn-action-danger"
                @click="closeConversation"
              >
                <v-icon size="15">mdi-close-circle-outline</v-icon>
                <span>Beenden</span>
              </button>
            </div>
          </div>

          <div class="chat-messages" ref="messagesContainer">
            <div
              v-for="msg in messages"
              :key="msg.id"
              class="message-wrapper"
              :class="{
                'msg-customer': msg.sender === 'customer',
                'msg-agent': msg.sender === 'agent',
                'msg-system': msg.sender === 'system',
              }"
            >
              <!-- Customer -->
              <div v-if="msg.sender === 'customer'" class="msg-row">
                <div class="msg-avatar msg-avatar-customer">
                  {{ selectedConversation.customerName.charAt(0) }}
                </div>
                <div class="msg-bubble bubble-customer">
                  <div class="msg-sender">{{ msg.senderName }}</div>
                  <div v-if="msg.imageUrl || msg.imageData" class="msg-image">
                    <img :src="msg.imageUrl || msg.imageData" @click="openImagePreview(msg.imageUrl || msg.imageData)" />
                  </div>
                  <div v-if="msg.message" class="msg-text">{{ msg.message }}</div>
                  <div class="msg-time">{{ formatMessageTime(msg.createdAt) }}</div>
                </div>
              </div>

              <!-- Agent -->
              <div v-else-if="msg.sender === 'agent'" class="msg-row msg-row-end">
                <div class="msg-bubble bubble-agent">
                  <div class="msg-sender">{{ msg.senderName }}</div>
                  <div v-if="msg.imageUrl || msg.imageData" class="msg-image">
                    <img :src="msg.imageUrl || msg.imageData" @click="openImagePreview(msg.imageUrl || msg.imageData)" />
                  </div>
                  <div v-if="msg.message" class="msg-text">{{ msg.message }}</div>
                  <div class="msg-time">{{ formatMessageTime(msg.createdAt) }}</div>
                </div>
                <div class="msg-avatar msg-avatar-agent">
                  <v-img src="/chatavatar.png" cover />
                </div>
              </div>

              <!-- System -->
              <div v-else class="msg-system-wrap">
                <span class="msg-system-pill">{{ msg.message }}</span>
              </div>
            </div>

            <div v-if="isCustomerTyping" class="message-wrapper msg-customer">
              <div class="msg-row">
                <div class="msg-avatar msg-avatar-customer">
                  {{ selectedConversation.customerName.charAt(0) }}
                </div>
                <div class="msg-bubble bubble-customer typing-bubble">
                  <div class="typing-dots">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Image preview above input -->
          <div v-if="imagePreview" class="image-preview-bar">
            <img :src="imagePreview" class="preview-thumb-sm" />
            <span class="preview-label">Bild ausgewählt</span>
            <div style="flex: 1"></div>
            <button class="icon-btn" @click="removeImagePreview"><v-icon size="16">mdi-close</v-icon></button>
          </div>

          <div class="chat-input-area">
            <input
              ref="imageInput"
              type="file"
              accept="image/*"
              style="display: none"
              @change="handleImageSelect"
            />
            <button
              class="input-icon-btn"
              :disabled="!isCurrentAgentInConversation"
              title="Bild anhängen"
              @click="$refs.imageInput.click()"
            >
              <v-icon size="18">mdi-paperclip</v-icon>
            </button>

            <div class="iw" :class="{ 'iw-f': inputFocused, 'iw-disabled': !isCurrentAgentInConversation }">
              <input
                v-model="messageInput"
                class="ii"
                placeholder="Nachricht schreiben…"
                :disabled="!isCurrentAgentInConversation"
                @keypress.enter="sendMessage"
                @input="handleTyping"
                @focus="inputFocused = true"
                @blur="inputFocused = false"
              />
            </div>

            <button
              class="send-btn"
              :disabled="(!messageInput.trim() && !imagePreview) || !isCurrentAgentInConversation"
              @click="sendMessage"
              title="Senden"
            >
              <v-icon size="18">mdi-send</v-icon>
            </button>
          </div>
        </template>
      </div>
    </div>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor === 'success' ? '#2E7D32' : snackbarColor === 'warning' ? '#E65100' : '#D32F2F'"
      timeout="3000"
      location="top"
    >
      <div class="d-flex align-center" style="gap: 8px">
        <v-icon size="18" color="white">
          {{ snackbarColor === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
        </v-icon>
        {{ snackbarText }}
      </div>
    </v-snackbar>

    <!-- Take Conversation Confirm Dialog -->
    <div v-if="showConfirmDialog" class="dialog-backdrop" @click="showConfirmDialog = false"></div>
    <v-dialog v-model="showConfirmDialog" max-width="500" :scrim="false">
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" @click="showConfirmDialog = false"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon" style="background: rgba(46,125,50,0.1)">
            <v-icon size="22" color="#2E7D32">mdi-account-check-outline</v-icon>
          </div>
          <div>
            <div class="dh-title">Chat übernehmen?</div>
            <div class="dh-sub">Nur ein aktiver Chat gleichzeitig</div>
          </div>
        </div>
        <div class="db">
          <p class="dialog-text">
            Möchtest du diesen Chat wirklich übernehmen? Du kannst erst dann einen anderen Chat öffnen, wenn du diesen verlässt oder beendest.
          </p>
        </div>
        <div class="dff">
          <button class="bo" @click="showConfirmDialog = false">Abbrechen</button>
          <button class="bp bp-success" @click="takeConversation">Übernehmen</button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Image Lightbox -->
    <div v-if="showImageDialog" class="dialog-backdrop" @click="showImageDialog = false"></div>
    <v-dialog v-model="showImageDialog" max-width="960" :scrim="false">
      <v-card rounded="xl" class="lightbox-card" elevation="0">
        <button class="lightbox-close" @click="showImageDialog = false"><v-icon color="white">mdi-close</v-icon></button>
        <img :src="previewImageUrl" style="width: 100%; height: auto; display: block" />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import io from 'socket.io-client';
import { useUserStore } from '@/store/user';
import axios from 'axios';

export default {
  name: 'LiveChatAdmin',

  setup() {
    const userStore = useUserStore();
    return { userStore };
  },

  data() {
    return {
      socket: null,
      conversations: [],
      selectedConversation: null,
      messages: [],
      messageInput: '',
      inputFocused: false,
      filter: 'all',
      isCustomerTyping: false,
      typingTimeout: null,
      employee: { id: null, name: '' },
      chatEnabled: true,
      toggling: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      currentlyJoinedConversationId: null,
      showConfirmDialog: false,
      pendingConversation: null,
      imagePreview: null,
      imageFile: null,
      showImageDialog: false,
      previewImageUrl: null,
    };
  },

  computed: {
    filteredConversations() {
      if (this.filter === 'all') return this.conversations;
      return this.conversations.filter((c) => c.status === this.filter);
    },
    waitingCount() { return this.conversations.filter((c) => c.status === 'waiting').length; },
    activeCount() { return this.conversations.filter((c) => c.status === 'active').length; },
    isCurrentAgentInConversation() {
      if (!this.selectedConversation || !this.currentlyJoinedConversationId) return false;
      return this.selectedConversation.id === this.currentlyJoinedConversationId;
    },
  },

  mounted() {
    this.loadEmployeeData();
    this.loadChatStatus();
    this.connectWebSocket();
    this.loadConversations();
  },

  beforeUnmount() {
    if (this.socket) this.socket.disconnect();
  },

  methods: {
    loadEmployeeData() {
      if (this.userStore.isAuthenticated) {
        this.employee.id = this.userStore.id;
        this.employee.name = this.userStore.name;
      } else {
        this.userStore.loadFromStorage();
        if (this.userStore.isAuthenticated) {
          this.employee.id = this.userStore.id;
          this.employee.name = this.userStore.name;
        } else {
          this.employee.id = 1;
          this.employee.name = 'Support Agent';
        }
      }
    },

    async loadChatStatus() {
      try {
        const response = await axios.get('https://sam.floow-media.de/api/livechat/status');
        if (response.data.success) this.chatEnabled = response.data.isEnabled;
      } catch (error) {
        console.error('Error loading chat status:', error);
      }
    },

    async toggleChat(value) {
      this.toggling = true;
      try {
        const response = await axios.post('https://sam.floow-media.de/api/livechat/toggle', {
          isEnabled: value,
          employeeId: this.employee.id,
        });
        if (response.data.success) {
          this.chatEnabled = value;
          this.snackbarText = response.data.message;
          this.snackbarColor = 'success';
          this.snackbar = true;
          if (this.socket) {
            this.socket.emit('chat:status_changed', { isEnabled: value, changedBy: this.employee.name });
          }
        }
      } catch (error) {
        console.error('Error toggling chat:', error);
        this.snackbarText = 'Fehler beim Ändern des Status';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.toggling = false;
      }
    },

    handleImageSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        this.snackbarText = 'Bitte wähle eine Bilddatei aus';
        this.snackbarColor = 'error';
        this.snackbar = true;
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.snackbarText = 'Bild darf maximal 5MB groß sein';
        this.snackbarColor = 'error';
        this.snackbar = true;
        return;
      }
      this.imageFile = file;
      const reader = new FileReader();
      reader.onload = (e) => { this.imagePreview = e.target.result; };
      reader.readAsDataURL(file);
    },

    removeImagePreview() {
      this.imagePreview = null;
      this.imageFile = null;
      if (this.$refs.imageInput) this.$refs.imageInput.value = '';
    },

    openImagePreview(imageUrl) {
      this.previewImageUrl = imageUrl;
      this.showImageDialog = true;
    },

    connectWebSocket() {
      this.socket = io('https://sam.floow-media.de', {
        transports: ['websocket', 'polling'],
        path: '/socket.io/',
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
        secure: true,
        rejectUnauthorized: false,
      });

      this.socket.on('connect', () => {
        this.socket.emit('admin:join', { employeeId: this.employee.id, employeeName: this.employee.name });
      });

      this.socket.on('admin:conversations_list', (conversations) => {
        this.conversations = conversations;
      });

      this.socket.on('admin:new_chat', (data) => {
        this.conversations.unshift(data.conversation);
        this.playNotificationSound();
      });

      this.socket.on('admin:conversation_loaded', (data) => {
        if (this.selectedConversation && this.selectedConversation.id === data.conversation.id) {
          this.messages = data.messages || [];
          Object.assign(this.selectedConversation, data.conversation);
          this.$nextTick(() => this.scrollToBottom());
        }
      });

      this.socket.on('admin:conversation_joined', (data) => {
        if (this.selectedConversation && this.selectedConversation.id === data.conversation.id) {
          this.messages = data.messages || [];
          Object.assign(this.selectedConversation, data.conversation);
          this.$nextTick(() => this.scrollToBottom());
        }
      });

      this.socket.on('message:received', (message) => {
        if (this.selectedConversation && message.conversationId === this.selectedConversation.id) {
          const exists = this.messages.find((m) => m.id === message.id);
          if (!exists) {
            this.messages.push(message);
            this.scrollToBottom();
          }
          if (this.isCurrentAgentInConversation) {
            this.socket.emit('messages:mark_read', { conversationId: this.selectedConversation.id });
          }
        }
      });

      this.socket.on('admin:conversation_updated', (data) => {
        const conv = this.conversations.find((c) => c.id === data.conversationId);
        if (conv) Object.assign(conv, data);
        if (this.selectedConversation && this.selectedConversation.id === data.conversationId) {
          Object.assign(this.selectedConversation, data);
        }
      });

      this.socket.on('typing:indicator', (data) => {
        if (data.userType === 'customer' && data.isTyping) {
          this.isCustomerTyping = true;
          this.scrollToBottom();
        } else {
          this.isCustomerTyping = false;
        }
      });

      this.socket.on('admin:new_message', (data) => {
        const conv = this.conversations.find((c) => c.id === data.conversationId);
        if (conv) {
          conv.lastMessage = data.message;
          if (this.currentlyJoinedConversationId !== data.conversationId) {
            conv.unreadCount = (conv.unreadCount || 0) + 1;
          }
          conv.updatedAt = data.message.createdAt;
          this.conversations = [conv, ...this.conversations.filter((c) => c.id !== conv.id)];
        }
      });

      this.socket.on('admin:agent_joined', (data) => {
        if (this.selectedConversation && this.selectedConversation.id === data.conversationId) {
          this.currentlyJoinedConversationId = data.conversationId;
          this.messages.push({
            id: `system-${Date.now()}`,
            conversationId: data.conversationId,
            sender: 'system',
            senderName: 'System',
            message: data.message,
            createdAt: new Date().toISOString(),
          });
          this.scrollToBottom();
        }
      });

      this.socket.on('admin:agent_left', (data) => {
        if (data.employeeId === this.employee.id) this.currentlyJoinedConversationId = null;
        if (this.selectedConversation && this.selectedConversation.id === data.conversationId) {
          this.messages.push({
            id: `system-${Date.now()}`,
            conversationId: data.conversationId,
            sender: 'system',
            senderName: 'System',
            message: data.message,
            createdAt: new Date().toISOString(),
          });
          this.scrollToBottom();
        }
      });

      this.socket.on('conversation:closed', (data) => {
        if (data.conversationId === this.currentlyJoinedConversationId) this.currentlyJoinedConversationId = null;
        const conv = this.conversations.find((c) => c.id === data.conversationId);
        if (conv) conv.status = 'closed';
        if (this.selectedConversation && this.selectedConversation.id === data.conversationId) {
          this.selectedConversation.status = 'closed';
        }
      });

      this.socket.on('error', (error) => {
        this.snackbarText = error.message || 'Ein Fehler ist aufgetreten';
        this.snackbarColor = 'error';
        this.snackbar = true;
      });
    },

    async loadConversations() {
      try {
        const response = await fetch('https://sam.floow-media.de/api/livechat/conversations?status=waiting,active');
        const data = await response.json();
        if (data.success) this.conversations = data.conversations;
      } catch (error) {
        console.error('Error loading conversations:', error);
      }
    },

    handleConversationClick(conv) {
      if (this.currentlyJoinedConversationId && this.currentlyJoinedConversationId !== conv.id) {
        this.snackbarText = 'Du musst erst den aktuellen Chat verlassen oder beenden';
        this.snackbarColor = 'warning';
        this.snackbar = true;
        return;
      }
      this.selectConversation(conv);
    },

    selectConversation(conv) {
      this.selectedConversation = conv;
      this.messages = [];
      if (!this.socket || !this.socket.connected) {
        this.snackbarText = 'Keine Verbindung zum Server';
        this.snackbarColor = 'error';
        this.snackbar = true;
        return;
      }
      this.socket.emit('admin:view_conversation', { conversationId: conv.id, employeeId: this.employee.id });
      conv.unreadCount = 0;
    },

    confirmTakeConversation() {
      if (this.currentlyJoinedConversationId) {
        this.snackbarText = 'Du musst erst den aktuellen Chat verlassen';
        this.snackbarColor = 'warning';
        this.snackbar = true;
        return;
      }
      if (this.selectedConversation.status === 'closed') {
        this.snackbarText = 'Dieser Chat wurde bereits beendet';
        this.snackbarColor = 'error';
        this.snackbar = true;
        return;
      }
      this.pendingConversation = this.selectedConversation;
      this.showConfirmDialog = true;
    },

    takeConversation() {
      if (!this.pendingConversation) return;
      this.showConfirmDialog = false;
      this.socket.emit('admin:take_conversation', {
        conversationId: this.pendingConversation.id,
        employeeId: this.employee.id,
        employeeName: this.employee.name,
      });
      this.pendingConversation = null;
    },

    leaveConversation() {
      if (!this.selectedConversation || !this.currentlyJoinedConversationId) return;
      if (confirm('Möchtest du diesen Chat wirklich verlassen?')) {
        this.socket.emit('admin:leave_conversation', {
          conversationId: this.selectedConversation.id,
          employeeId: this.employee.id,
          employeeName: this.employee.name,
        });
      }
    },

    closeConversation() {
      if (!this.selectedConversation) return;
      if (confirm('Möchtest du diesen Chat wirklich beenden?')) {
        this.socket.emit('conversation:close', {
          conversationId: this.selectedConversation.id,
          closedBy: this.employee.name,
        });
        this.selectedConversation = null;
        this.messages = [];
      }
    },

    async sendMessage() {
      if ((!this.messageInput.trim() && !this.imagePreview) || !this.selectedConversation) return;
      if (!this.isCurrentAgentInConversation) {
        this.snackbarText = 'Du musst den Chat erst übernehmen';
        this.snackbarColor = 'warning';
        this.snackbar = true;
        return;
      }
      const message = this.messageInput.trim();
      const messageData = {
        conversationId: this.selectedConversation.id,
        message,
        sender: 'agent',
        senderName: this.employee.name,
      };
      if (this.imagePreview) messageData.imageData = this.imagePreview;
      this.socket.emit('message:send', messageData);
      this.messageInput = '';
      this.removeImagePreview();
      this.scrollToBottom();
      this.socket.emit('typing:stop', { conversationId: this.selectedConversation.id, userType: 'agent' });
    },

    handleTyping() {
      if (!this.selectedConversation || !this.isCurrentAgentInConversation) return;
      this.socket.emit('typing:start', {
        conversationId: this.selectedConversation.id,
        userType: 'agent',
        userName: this.employee.name,
      });
      clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(() => {
        this.socket.emit('typing:stop', { conversationId: this.selectedConversation.id, userType: 'agent' });
      }, 1000);
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) container.scrollTop = container.scrollHeight;
      });
    },

    formatTime(timestamp) {
      const date = new Date(timestamp);
      const diff = new Date() - date;
      if (diff < 60000) return 'Jetzt';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
    },

    formatMessageTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    },

    playNotificationSound() {
      const audio = new Audio('/notification.mp3');
      audio.play().catch(() => {});
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 26px; font-weight: 800; color: #0a1628; letter-spacing: -0.3px; }
.page-subtitle { font-size: 13px; color: #888; }

/* Stats */
.stat-row { display: flex; gap: 10px; flex-wrap: wrap; }
.stat-card {
  background: white; border: 1px solid #eceef2; border-radius: 12px;
  padding: 12px 16px; min-width: 110px;
}
.stat-label { font-size: 11px; color: #999; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; }
.stat-value { font-size: 18px; font-weight: 700; color: #0a1628; }
.stat-value-row { display: flex; align-items: center; gap: 8px; }
.stat-dot { width: 8px; height: 8px; border-radius: 50%; }
.stat-dot-active { background: #2E7D32; }
.stat-dot-waiting { background: #FB8C00; }

.stat-toggle { display: flex; align-items: center; gap: 14px; }
.toggle-switch {
  position: relative; width: 42px; height: 24px; border-radius: 12px;
  background: #e0e2e6; border: none; cursor: pointer; padding: 0;
  transition: background 0.2s; flex-shrink: 0;
}
.toggle-switch:disabled { opacity: 0.6; cursor: not-allowed; }
.toggle-thumb {
  position: absolute; top: 2px; left: 2px;
  width: 20px; height: 20px; border-radius: 50%;
  background: white; box-shadow: 0 1px 3px rgba(0,0,0,0.15);
  transition: transform 0.2s;
}
.toggle-on { background: #2E7D32; }
.toggle-on .toggle-thumb { transform: translateX(18px); }

/* Alert */
.alert-banner {
  display: flex; align-items: center; gap: 14px;
  background: white; border: 1px solid #eceef2;
  border-left: 3px solid #E65100; border-radius: 12px;
  padding: 14px 18px;
}
.alert-icon {
  width: 36px; height: 36px; min-width: 36px; border-radius: 10px;
  background: rgba(255,152,0,0.1); display: flex; align-items: center; justify-content: center;
}
.alert-title { font-size: 14px; font-weight: 700; color: #0a1628; }
.alert-sub { font-size: 12px; color: #888; margin-top: 2px; }

/* Chat Container */
.chat-container {
  display: grid;
  grid-template-columns: 340px 1fr;
  height: calc(100vh - 260px);
  min-height: 520px;
  background: white;
  border: 1px solid #eceef2;
  border-radius: 16px;
  overflow: hidden;
}

/* Conversations column */
.conversations-column {
  display: flex; flex-direction: column;
  border-right: 1px solid #eceef2;
  background: #fafbfc;
}
.conv-header { padding: 18px 20px; border-bottom: 1px solid #eceef2; background: white; }
.conv-title { font-size: 14px; font-weight: 700; color: #0a1628; }

.seg-toggle {
  display: inline-flex; align-items: center; gap: 4px; width: 100%;
  padding: 4px; background: #f8f9fb; border: 1px solid #eceef2; border-radius: 10px;
}
.seg-btn {
  flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 6px 10px; background: transparent; border: none; border-radius: 7px;
  font-size: 12px; font-weight: 600; color: #666; cursor: pointer;
  transition: all 0.15s; font-family: inherit;
}
.seg-btn:hover:not(.seg-active) { background: white; color: #0a1628; }
.seg-active { background: white; color: #0a1628; box-shadow: 0 1px 3px rgba(10,22,40,0.08); }
.seg-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px;
  font-size: 10px; font-weight: 700;
}
.seg-count-warning { background: #FB8C00; color: white; }
.seg-count-success { background: #2E7D32; color: white; }

.conv-list { flex: 1; overflow-y: auto; padding: 8px; }
.conv-item {
  display: flex; align-items: center; gap: 12px; width: 100%;
  padding: 12px; margin-bottom: 4px;
  background: transparent; border: 1px solid transparent; border-radius: 10px;
  cursor: pointer; transition: all 0.15s; text-align: left; font-family: inherit;
}
.conv-item:hover { background: white; border-color: #eceef2; }
.conv-active { background: white !important; border-color: #0a1628 !important; }
.conv-unread:not(.conv-active) { background: rgba(251,140,0,0.06); border-color: rgba(251,140,0,0.2); }

.conv-avatar {
  width: 40px; height: 40px; min-width: 40px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 700; color: white; background: #5B5BD6;
}
.conv-avatar-waiting { background: #FB8C00; }
.conv-avatar-active { background: #2E7D32; }
.conv-avatar-closed { background: #9E9E9E; }
.conv-avatar-header { width: 44px; height: 44px; min-width: 44px; font-size: 16px; }

.conv-content { flex: 1; min-width: 0; }
.conv-row { display: flex; align-items: center; justify-content: space-between; gap: 6px; margin-bottom: 2px; }
.conv-name {
  font-size: 13px; font-weight: 700; color: #0a1628;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.conv-time { font-size: 10px; color: #999; white-space: nowrap; flex-shrink: 0; }
.conv-preview {
  font-size: 12px; color: #888;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

.conv-badges { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; flex-shrink: 0; }
.unread-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px;
  background: #D32F2F; color: white; font-size: 10px; font-weight: 700;
}
.status-dot { width: 8px; height: 8px; border-radius: 50%; }
.status-waiting { background: #FB8C00; }
.status-active { background: #2E7D32; }
.status-closed { background: #9E9E9E; }

.empty-state-inline {
  display: flex; flex-direction: column; align-items: center;
  padding: 48px 16px; text-align: center;
}
.empty-title { font-size: 15px; font-weight: 700; color: #0a1628; margin-bottom: 6px; }
.empty-desc { font-size: 12px; color: #888; max-width: 280px; }

/* Chat column */
.chat-column { display: flex; flex-direction: column; min-width: 0; background: white; }
.chat-empty {
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; padding: 40px;
}

.chat-header {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 16px 20px; border-bottom: 1px solid #eceef2;
}
.chat-header-user { display: flex; align-items: center; gap: 12px; }
.chat-user-name { font-size: 14px; font-weight: 700; color: #0a1628; }
.chat-user-sub { font-size: 12px; color: #888; margin-top: 2px; }

.chat-header-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.btn-action {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px; border: 1px solid transparent; border-radius: 9px;
  font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.15s;
  font-family: inherit; background: white;
}
.btn-action:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-action-success { color: #2E7D32; border-color: rgba(46,125,50,0.25); }
.btn-action-success:hover:not(:disabled) { background: rgba(46,125,50,0.06); border-color: #2E7D32; }
.btn-action-warning { color: #E65100; border-color: rgba(255,152,0,0.3); }
.btn-action-warning:hover { background: rgba(255,152,0,0.06); border-color: #E65100; }
.btn-action-danger { color: #D32F2F; border-color: rgba(211,47,47,0.25); }
.btn-action-danger:hover { background: rgba(211,47,47,0.06); border-color: #D32F2F; }

/* Messages */
.chat-messages {
  flex: 1; overflow-y: auto; overflow-x: hidden;
  padding: 20px; background: #fafbfc;
  display: flex; flex-direction: column; gap: 12px;
}

.message-wrapper { display: flex; animation: slideIn 0.2s ease; }
@keyframes slideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

.msg-customer { justify-content: flex-start; }
.msg-agent { justify-content: flex-end; }
.msg-system { justify-content: center; }

.msg-row { display: flex; align-items: flex-end; gap: 8px; max-width: 75%; }
.msg-row-end { flex-direction: row; }

.msg-avatar {
  width: 32px; height: 32px; min-width: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: white; flex-shrink: 0;
  overflow: hidden;
}
.msg-avatar-customer { background: #5B5BD6; }
.msg-avatar-agent { background: #0a1628; }

.msg-bubble {
  padding: 10px 14px; border-radius: 14px;
  min-width: 140px; max-width: 100%;
  word-wrap: break-word;
}
.bubble-customer { background: white; border: 1px solid #eceef2; }
.bubble-agent { background: #0a1628; color: white; }

.msg-sender { font-size: 11px; font-weight: 700; opacity: 0.75; margin-bottom: 2px; }
.bubble-customer .msg-sender { color: #5B5BD6; opacity: 1; }
.bubble-agent .msg-sender { color: rgba(255,255,255,0.6); }
.msg-text { font-size: 13px; line-height: 1.5; color: #0a1628; white-space: pre-wrap; }
.bubble-agent .msg-text { color: white; }
.msg-time { font-size: 10px; opacity: 0.55; margin-top: 4px; }
.bubble-agent .msg-time { color: rgba(255,255,255,0.7); opacity: 1; }

.msg-image { margin: 4px 0; }
.msg-image img {
  max-width: 100%; max-height: 260px; border-radius: 10px;
  cursor: zoom-in; transition: transform 0.15s;
}
.msg-image img:hover { transform: scale(1.02); }

.msg-system-wrap { display: flex; justify-content: center; }
.msg-system-pill {
  padding: 4px 12px; border-radius: 12px;
  background: #f0f2f5; color: #666;
  font-size: 11px; font-weight: 600;
}

.typing-bubble { padding: 12px 16px; min-width: 60px; }
.typing-dots { display: flex; gap: 4px; align-items: center; }
.typing-dots span {
  width: 7px; height: 7px; border-radius: 50%;
  background: #c5cbd3; animation: typing 1.4s infinite ease-in-out;
}
.typing-dots span:nth-child(1) { animation-delay: 0s; }
.typing-dots span:nth-child(2) { animation-delay: 0.2s; }
.typing-dots span:nth-child(3) { animation-delay: 0.4s; }
@keyframes typing {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-6px); opacity: 1; }
}

/* Image preview above input */
.image-preview-bar {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 16px; background: #f8f9fb; border-top: 1px solid #eceef2;
}
.preview-thumb-sm {
  width: 40px; height: 40px; object-fit: cover; border-radius: 8px;
  border: 1px solid #eceef2;
}
.preview-label { font-size: 12px; color: #555; font-weight: 500; }
.icon-btn {
  width: 28px; height: 28px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: 1px solid transparent; border-radius: 8px; cursor: pointer;
  color: #666; transition: all 0.15s;
}
.icon-btn:hover { background: #f0f2f5; color: #0a1628; }

/* Input area */
.chat-input-area {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 16px; border-top: 1px solid #eceef2; background: white;
}
.input-icon-btn {
  width: 38px; height: 38px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: 1px solid #eceef2; border-radius: 10px; cursor: pointer;
  color: #666; transition: all 0.15s; flex-shrink: 0;
}
.input-icon-btn:hover:not(:disabled) { background: #f8f9fb; color: #0a1628; }
.input-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.iw {
  flex: 1; display: flex; align-items: center; gap: 10px;
  height: 42px; padding: 0 14px;
  background: #f8f9fb; border: 1px solid #eceef2; border-radius: 10px;
  transition: all 0.15s;
}
.iw.iw-f { border-color: #0a1628; background: white; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.iw.iw-disabled { opacity: 0.6; background: #f0f2f5; }
.ii {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 14px; color: #0a1628; font-weight: 500; min-width: 0;
  font-family: inherit; height: 40px;
}
.ii::placeholder { color: #aaa; font-weight: 400; }

.send-btn {
  width: 42px; height: 42px; display: flex; align-items: center; justify-content: center;
  background: #2E7D32; border: none; border-radius: 10px; cursor: pointer;
  color: white; transition: background 0.15s; flex-shrink: 0;
}
.send-btn:hover:not(:disabled) { background: #1B5E20; }
.send-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Scrollbars */
.conv-list::-webkit-scrollbar,
.chat-messages::-webkit-scrollbar { width: 6px; }
.conv-list::-webkit-scrollbar-thumb,
.chat-messages::-webkit-scrollbar-thumb {
  background: rgba(10,22,40,0.15); border-radius: 3px;
}

/* Dialog */
.dialog-backdrop { position: fixed; inset: 0; background: rgba(10,22,40,0.5); z-index: 2000; animation: fadeBg 0.2s ease; }
@keyframes fadeBg { from { opacity: 0; } to { opacity: 1; } }
.modern-dialog {
  background: white !important;
  box-shadow: 0 24px 64px rgba(10,22,40,0.18) !important;
  z-index: 2001 !important; position: relative;
  animation: slideUpDlg 0.3s cubic-bezier(0.16,1,0.3,1);
  overflow: visible !important;
}
@keyframes slideUpDlg { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
.close-btn {
  position: absolute; top: 16px; right: 16px; z-index: 10;
  width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 8px; cursor: pointer; color: #888;
  transition: all 0.2s;
}
.close-btn:hover { background: #f0f2f5; color: #0a1628; transform: rotate(90deg); }
.dh { display: flex; align-items: center; gap: 14px; padding: 28px 28px 0; }
.dh-icon {
  width: 44px; height: 44px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(10,22,40,0.06); flex-shrink: 0;
}
.dh-title { font-size: 18px; font-weight: 700; color: #0a1628; }
.dh-sub { font-size: 12px; color: #999; margin-top: 2px; }
.db { padding: 20px 28px; }
.dff { display: flex; align-items: center; justify-content: flex-end; gap: 10px; padding: 4px 28px 24px; }
.dialog-text { font-size: 13px; color: #555; line-height: 1.6; }

.bo {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 18px; border: 1px solid #eceef2; border-radius: 10px;
  background: white; font-size: 13px; font-weight: 600; color: #555;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.bo:hover { border-color: #0a1628; }
.bp {
  display: inline-flex; align-items: center; gap: 6px; min-width: 120px;
  padding: 10px 20px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
  cursor: pointer; justify-content: center; font-family: inherit;
  transition: background 0.15s;
}
.bp:hover { background: #1d3457; }
.bp-success { background: #2E7D32; }
.bp-success:hover { background: #1B5E20; }

/* Lightbox */
.lightbox-card { background: #0f172a !important; position: relative; padding: 0 !important; overflow: hidden !important; }
.lightbox-close {
  position: absolute; top: 12px; right: 12px; z-index: 10;
  width: 36px; height: 36px; display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.15); border: none; border-radius: 8px;
  cursor: pointer; backdrop-filter: blur(8px);
}
.lightbox-close:hover { background: rgba(255,255,255,0.25); }

@media (max-width: 960px) {
  .chat-container {
    grid-template-columns: 1fr;
    height: auto;
    min-height: calc(100vh - 260px);
  }
  .conversations-column { border-right: none; border-bottom: 1px solid #eceef2; }
  .msg-row { max-width: 85%; }
  .stat-row { width: 100%; }
}
</style>
